import React, { useEffect, useMemo, useState } from "react";
import { Tabs, Tab, Box } from "@mui/material";
import CommonCardsList, { CardItem } from "../Common/CommonCardList";
import InsightsMagicSvg from "../../assets/meeting/insights-magic.svg";
import FeedbackPopup from "../Common/FeedbackPopUp";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import { capitalizeFirstLetter } from "../../utils/utils";

interface CallQualityQuestion {
  question: string;
  answer: string;
  reason: string;
  reverse_score?: boolean;
  question_display_order?: number | null;
  mandatory_question_passed?: boolean | null;
}
interface CallQualitySection {
  score: string;
  final_score?: string;
  scoring_tag?: string;
  reason: string;
  questions: CallQualityQuestion[];
  what_went_well: string[];
  areas_of_opportunity: string[];
  category_display_order?: number | null;
  mandatory_questions_passed?: boolean | null;
}

interface CallQualityData {
  [key: string]: CallQualitySection | undefined;
}

interface InteractionCallQualityProps {
  callQualityData?: CallQualityData;
  interactionId?: string;
}

const InteractionCallQuality: React.FC<InteractionCallQualityProps> = ({
  callQualityData,
  interactionId,
}) => {
  const [selectedTab, setSelectedTab] = useState<string>("");
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState<number | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [feedbackReference, setFeedbackReference] = useState<string>("");
  const { showSnackbar } = useDisplaySnackbar();

  /** Helper: check if section actually has any data to display */
  const hasSectionData = (section?: CallQualitySection): boolean => {
    if (!section) return false;
    const {
      questions,
      what_went_well,
      areas_of_opportunity,
    } = section;
    return (
      (Array.isArray(questions) && questions.length > 0) ||
      (Array.isArray(what_went_well) && what_went_well.length > 0) ||
      (Array.isArray(areas_of_opportunity) && areas_of_opportunity.length > 0)
    );
  };

  const tabPriority = ["opening", "execution", "closing"];

  const getPriorityIndex = (key: string) => {
    const lowerKey = key.toLowerCase();

    return tabPriority.findIndex(priority =>
        lowerKey.includes(priority)
      );
  };

  /** Prepare available tabs dynamically */
  const availableTabs = useMemo(() => {
    if (!callQualityData) return [];

    const sections = Object.entries(callQualityData)
      .filter(([_, section]) => hasSectionData(section))
      .sort(([a], [b]) => {
        // Ensure priority order, others follow after
        const idxA = getPriorityIndex(a);
        const idxB = getPriorityIndex(b);
        if (idxA === -1 && idxB === -1) return a.localeCompare(b);
        if (idxA === -1) return 1;
        if (idxB === -1) return -1;
        return idxA - idxB;
      });

    return sections.map(([key]) => key);
  }, [callQualityData]);

  useEffect(() => {
    if (availableTabs.length > 0) {
      setSelectedTab(`${availableTabs[0]}-tab`);
    } else {
      setSelectedTab("");
    }
  }, [availableTabs]);

  const handleChange = (_: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  const sharedIcon = (
    <img src={InsightsMagicSvg} alt="insight" />
  );
  const formatScore = (score?: string) => {
    if (!score) return "";
    // Check if it looks like a fraction or numeric (e.g., "1/2", "0/1")
    if (/^\d+\/\d+$/.test(score)) return score;
    // Otherwise, capitalize first letter
    return score.charAt(0).toUpperCase() + score.slice(1).toLowerCase();
  };  

  const getStatusColor = (tag?: string, score?: string) => {
    const tagValue = tag?.toLowerCase();
    const scoreValue = score?.toLowerCase();
  
    if (tagValue) {
      const tagColorMap: Record<string, string> = {
        positive: "#2BB673",
        "close watch": "#FFC700",
        immediate: "#FF2E00",
      };
      if (tagColorMap[tagValue]) return tagColorMap[tagValue];
    }
  
    const scoreColorMap: Record<string, string> = {
      positive: "#2BB673",
      negative: "#FF2E00",
      neutral: "#FFC700",
    };
  
    return scoreColorMap[scoreValue || ""] || "#000000";
  };

  const mapSectionToCards = (
    section: CallQualitySection | undefined,
    title: string
  ): CardItem[] => {
    if (!section) return [];
    const tag = section?.scoring_tag?.trim();
    return [
      {
        key: `${title}-card`,
        icon: sharedIcon,
        title,
        status: tag ? tag : formatScore(section.score),
        statusColor: getStatusColor(tag, section.score),
        paragraph: section.questions,
        listA: {
          title: "What went well?",
          items: (section.what_went_well || []).map(capitalizeFirstLetter),
        },
        listB: {
          title: "Areas of opportunity:",
          items: (section.areas_of_opportunity || []).map(capitalizeFirstLetter),
        },
        onFeedbackClick: (index, answer) => {
          const questionText =
            index !== undefined && section?.questions?.[index]
              ? section.questions[index].question
              : "";
          setSelectedQuestionIndex(index ?? null);
          setSelectedAnswer(answer ?? null);
          setFeedbackReference(questionText);
          setFeedbackOpen(true);
        },
      },
    ];
  };

  const callQualityKey:
  | "abstraction_call_quality"
  | `abstraction_call_quality_${string}` =
  selectedTab
    ? `abstraction_call_quality_${selectedTab.replace("-tab", "")}`
    : "abstraction_call_quality";

  const formatLabel = (key: string) =>
  key
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
  return (
    <Box className="interaction-call-quality-wrapper">
      <Box className="interaction-call-quality-tabs-header">
        <Tabs
          value={selectedTab}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          className="app-tabs-container"
        >
          {availableTabs.map((key) => (
              <Tab
                key={key}
                label={formatLabel(key)}
                value={`${key}-tab`}
              />
            ))}
        </Tabs>
      </Box>
      <Box className="interaction-call-quality-content">
        <Box>
        {availableTabs.map((key) => {
          const section = callQualityData?.[key];
          const cards = mapSectionToCards(
            section,
            `${formatLabel(key)} Score:`
          );

          return (
            selectedTab === `${key}-tab` && (
              <CommonCardsList key={key} cards={cards} />
            )
          );
        })}
        </Box>
      </Box>
      <FeedbackPopup
        open={feedbackOpen}
        interactionId={interactionId || ""}
        feedbackKey={callQualityKey}
        feedbackReference={feedbackReference}
        answer={selectedAnswer}
        onClose={() => setFeedbackOpen(false)}
        onSubmit={(data) => {
          console.log("Feedback submitted:", {
            questionIndex: selectedQuestionIndex,
            answer: selectedAnswer,
            ...data,
          });
          showSnackbar("success", "Feedback submitted successfully.");
        }}
      />
    </Box>
  );
};

export default InteractionCallQuality;
